import { Component, computed, inject, input, viewChild } from '@angular/core';
import { WarehouseMaterialOperationTableStore } from "./warehouse-material-operation-table.store";
import { TableColumn } from "packages/client/src/app/modules/shared/components/kit/table/table.types";
import { TableComponent } from "packages/client/src/app/modules/shared/components/kit/table/table.component";
import { getWarehouseMaterialOperationsFrag } from "./warehouse-material-operation-table.query";
import { WarehouseMaterialOperationObject } from "@libs/types/src/services/object-type/service/social-service/warehouse-material-operation.object";
import { dateColumn } from "packages/client/src/app/modules/shared/components/kit/table/table.fields";
import { BaseDialogService } from "packages/client/src/app/components/kit/base-dialog/base-dialog-service";
import { ConfirmBuilder } from "packages/client/src/app/modules/shared/service/confirm.builder";
import { checkOk } from "packages/client/src/app/modules/content/content.error";
import { WarehouseMaterialOperationDialogComponent } from "../warehouse-material-operation-dialog/warehouse-material-operation-dialog.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { routeParams } from "packages/client/src/app/modules/shared/shared.global-service";
import { UserChipComponent } from "packages/client/src/app/components/kit/user-chip/user-chip.component";
import { WarehouseMaterialOperationTypeEnum } from "@libs/types/src/services/enum/warehouse-material-operation.enum";
import {
    FolderWarehouseMaterialOperationDialogComponent,
} from "packages/client/src/app/components/social-service/folder/warehouse-material/warehouse-material-operation-dialog/warehouse-material-operation-dialog.component";
import { workUri } from "packages/client/src/app/utils/router.utils";
import { Router } from "@angular/router";
import { DatePipe } from "@angular/common";
import { FilterBuilder } from "@libs/shared/utils/grid.builder";
import { PrimeTemplate } from "@openng/optimus-ui/api";
import { FormatPersonPipe } from "packages/client/src/app/modules/shared/pipes/format-person.pipe";

@Component({
    selector: 'app-warehouse-material-operation-table',
    templateUrl: './warehouse-material-operation-table.component.html',
    providers: [WarehouseMaterialOperationTableStore],
    imports: [
        TableComponent,
        TranslatePipe,
        UserChipComponent,
        DatePipe,
        PrimeTemplate,
        FormatPersonPipe,
    ],
})
export class WarehouseMaterialOperationTableComponent {

    private readonly router = inject(Router);

    materialId = input<string | null>(null);
    folderId = input<number | null, string | number | null>(null, { transform: v => v ? Number(v) : null});
    serviceId = input<number | null>(null);
    workId = input<number | null>(null);
    defaultDate = input<Date | null>(null);
    defaultWorkerIds = input<string[]>([]);
    workOperation = input(false);
    disabled = input(false);
    columns = input<TableColumn[]>([
        {
            name: 'type',
        },
        {
            name: 'amount',
        },
        dateColumn,
        {
            name: 'folderId',
        },
        {
            name: 'workers',
        },
        {
            name: 'description',
        },
    ])
    tableFrag = input<string>(getWarehouseMaterialOperationsFrag)

    protected readonly table = viewChild<TableComponent>('tableOperations');
    protected readonly store = inject(WarehouseMaterialOperationTableStore);
    protected readonly dialogService = inject(BaseDialogService);
    protected readonly confirm = inject(ConfirmBuilder);
    private readonly translate = inject(TranslateService);
    protected readonly prefix = 'service.warehouseMaterialOperation.'
    protected readonly WarehouseMaterialOperationTypeEnum = WarehouseMaterialOperationTypeEnum

    protected readonly additionalFilters = computed(() => {
        const filters = new FilterBuilder({
            serviceId: this.serviceId() || this.store.globalGlobalStore.currentService()?.id || 0,
            folderId: this.folderId(),
            workId: this.workId(),
            warehouseMaterialId: this.materialId(),
        })

        return filters.filter().filter
    })

    protected create() {
        if (this.disabled()) return;

        if (this.materialId()) {
            this.openDialogFromService(null);
        } else {
            this.openDialogFromFolder(null);
        }
    }

    protected edit(event: WarehouseMaterialOperationObject) {
        if (this.disabled()) return;

        if (this.materialId()) {
            this.openDialogFromService(event);
        } else {
            this.openDialogFromFolder(event);
        }
    }

    protected handleDeleteItemClick(event: any) {
        if (this.disabled()) return;

        this.confirm.confirm(
            this.prefix + 'deleteConfirmation.header',
            this.prefix + 'deleteConfirmation.label',
            () => this.deleteItem(event.item.id),
            { name: 'warehouseMaterialOperationDelete' + event.item.id },
            'pi-trash',
            event.event.target,
        );
    }

    protected async goToWorkDetail(item: any): Promise<void> {
        await this.router.navigateByUrl(workUri(item, item.serviceId));
    }

    private async deleteItem(id: WarehouseMaterialOperationObject['id']) {
        const res = await this.store.api.deleteWarehouseMaterialOperation({ id });

        checkOk(res, 'delete') && this.table()?.reload();
    }

    private openDialogFromFolder(item: WarehouseMaterialOperationObject | null) {
        const ref = this.dialogService.open(
            FolderWarehouseMaterialOperationDialogComponent,
            {
                header: this.translate.instant(this.prefix + (item ? 'editHeader' : 'createHeader')),
                styleClass: 'p-dialog-width-30',
                data: {
                    folderId: this.folderId(),
                    workId: this.workId(),
                    serviceId: Number(this.serviceId() || this.store.globalGlobalStore.currentService()?.id || 0),
                    date: this.defaultDate(),
                    workerIds: this.defaultWorkerIds(),
                    workOperation: this.workOperation(),
                    item,
                },
            });

        ref?.onClose?.subscribe((res) => {
            if (res?.refresh) {
                this.table()?.reload();
            }
        });
    }

    private openDialogFromService(item: WarehouseMaterialOperationObject | null) {
        const ref = this.dialogService.open(
            WarehouseMaterialOperationDialogComponent,
            {
                header: this.translate.instant(this.prefix + (item ? 'editHeader' : 'createHeader')),
                styleClass: 'p-dialog-width-30',
                data: {
                    materialId: Number(this.materialId()),
                    serviceId: Number(routeParams.serviceId()),
                    item,
                },
            });

        ref?.onClose?.subscribe((res) => {
            if (res?.refresh) {
                this.table()?.reload();
            }
        });
    }
}
